import React, { useState } from "react";

import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaFacebookF,
  FaYoutube,
  FaArrowRight,
  FaPaperPlane,
  FaChevronRight,
  FaClock,
  FaGraduationCap,
  FaBookOpen,
  FaUsers,
  FaLaptopCode,
  FaCheckCircle,
} from "react-icons/fa";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [message, setMessage] = useState("");
  const [messageSent, setMessageSent] = useState(false);

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (!message.trim()) return;

    setMessageSent(true);
    setMessage("");

    setTimeout(() => {
      setMessageSent(false);
    }, 3500);
  };

  return (
    <footer className="zion-footer">
      <style>{`
        .zion-footer {
          --zion-black: #080808;
          --zion-deep-black: #050505;
          --zion-charcoal: #111111;
          --zion-soft-black: #171717;

          --zion-gold: #d4a017;
          --zion-rich-gold: #b8860b;
          --zion-bright-gold: #f4c542;
          --zion-light-gold: #ffe8a3;
          --zion-pale-gold: #fff7dc;

          --zion-white: #ffffff;
          --zion-ivory: #f8f5ec;
          --zion-text: #d8d4ca;
          --zion-muted: #98948b;
          --zion-border: rgba(244, 197, 66, 0.18);

          position: relative;
          width: 100%;
          overflow: hidden;
          background: var(--zion-black);
          color: var(--zion-white);
          font-family: "Poppins", "Segoe UI", Arial, sans-serif;
          box-sizing: border-box;
        }

        .zion-footer *,
        .zion-footer *::before,
        .zion-footer *::after {
          box-sizing: border-box;
        }

        /* ==========================================
           TOP GOLD ACCENT
        ========================================== */

        .zion-footer-accent {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          width: 100%;
          height: 7px;
        }

        .zion-footer-accent span:nth-child(1) {
          background: var(--zion-rich-gold);
        }

        .zion-footer-accent span:nth-child(2) {
          background: var(--zion-bright-gold);
        }

        .zion-footer-accent span:nth-child(3) {
          background: var(--zion-rich-gold);
        }

        /* ==========================================
           MAIN FOOTER
        ========================================== */

        .zion-footer-main {
          position: relative;
          display: grid;
          grid-template-columns: 1.3fr 0.8fr 1.1fr;
          gap: 55px;
          width: 100%;
          max-width: 1450px;
          margin: 0 auto;
          padding: 75px 55px 65px;
        }

        .zion-footer-main::before {
          content: "";
          position: absolute;
          width: 360px;
          height: 360px;
          right: -180px;
          top: -170px;
          border: 1px solid rgba(244, 197, 66, 0.08);
          border-radius: 50%;
          pointer-events: none;
        }

        .zion-footer-main::after {
          content: "";
          position: absolute;
          width: 220px;
          height: 220px;
          right: -80px;
          top: -100px;
          border: 1px solid rgba(212, 160, 23, 0.15);
          border-radius: 50%;
          pointer-events: none;
        }

        /* ==========================================
           BRAND
        ========================================== */

        .zion-footer-brand-section {
          position: relative;
          z-index: 2;
          min-width: 0;
        }

        .zion-footer-brand {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
          min-width: 0;
        }

        .zion-footer-brand-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 58px;
          height: 58px;
          flex-shrink: 0;
          border: 1px solid rgba(244, 197, 66, 0.45);
          border-radius: 14px;
          background: linear-gradient(
            135deg,
            var(--zion-rich-gold),
            var(--zion-bright-gold)
          );
          color: var(--zion-black);
          font-size: 27px;
          box-shadow: 0 8px 25px rgba(212, 160, 23, 0.18);
          transition: 0.3s ease;
        }

        .zion-footer-brand:hover .zion-footer-brand-icon {
          transform: translateY(-4px) rotate(-3deg);
        }

        .zion-footer-brand-text {
          min-width: 0;
        }

        .zion-footer-brand-text h2 {
          margin: 0 0 5px;
          color: var(--zion-white);
          font-size: 22px;
          font-weight: 800;
          line-height: 1.2;
          letter-spacing: -0.4px;
          overflow-wrap: anywhere;
        }

        .zion-footer-brand-text span {
          color: var(--zion-bright-gold);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .zion-footer-description {
          max-width: 560px;
          margin: 0 0 27px;
          color: var(--zion-text);
          font-size: 14px;
          line-height: 1.9;
        }

        /* ==========================================
           VALUES
        ========================================== */

        .zion-footer-values {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 30px;
        }

        .zion-footer-value {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          padding: 9px 13px;
          border: 1px solid rgba(244, 197, 66, 0.12);
          border-radius: 7px;
          background: rgba(255, 255, 255, 0.025);
          color: #dedbd2;
          font-size: 12px;
          font-weight: 600;
          transition: 0.3s ease;
        }

        .zion-footer-value svg {
          color: var(--zion-bright-gold);
          font-size: 13px;
          flex-shrink: 0;
        }

        .zion-footer-value:hover {
          border-color: rgba(244, 197, 66, 0.35);
          background: rgba(212, 160, 23, 0.08);
          transform: translateY(-2px);
        }

        /* ==========================================
           SOCIAL
        ========================================== */

        .zion-footer-social-area {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .zion-footer-social-area > span {
          color: var(--zion-muted);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .zion-footer-social-links {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .zion-footer-social-links a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: 1px solid rgba(244, 197, 66, 0.18);
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.035);
          color: #d8d5cd;
          font-size: 13px;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .zion-footer-social-links a:hover {
          border-color: var(--zion-bright-gold);
          background: var(--zion-bright-gold);
          color: var(--zion-black);
          transform: translateY(-4px);
        }

        /* ==========================================
           NAVIGATION
        ========================================== */

        .zion-footer-navigation-section {
          display: grid;
          grid-template-columns: 1fr;
          gap: 42px;
          min-width: 0;
        }

        .zion-footer-column {
          position: relative;
          z-index: 2;
          min-width: 0;
        }

        .zion-footer-title {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 22px;
          min-width: 0;
        }

        .zion-footer-title-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          flex-shrink: 0;
          border: 1px solid rgba(244, 197, 66, 0.2);
          border-radius: 8px;
          background: rgba(212, 160, 23, 0.12);
          color: var(--zion-bright-gold);
          font-size: 13px;
        }

        .zion-footer-title h3 {
          margin: 0;
          color: var(--zion-white);
          font-size: 16px;
          font-weight: 750;
        }

        .zion-footer-column ul {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .zion-footer-column li {
          margin-bottom: 12px;
        }

        .zion-footer-column li a {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          max-width: 100%;
          color: #aaa69d;
          font-size: 13px;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .zion-footer-column li a svg {
          color: var(--zion-bright-gold);
          font-size: 9px;
          transition: 0.3s ease;
          flex-shrink: 0;
        }

        .zion-footer-column li a:hover {
          color: var(--zion-bright-gold);
          transform: translateX(5px);
        }

        .zion-footer-column li a:hover svg {
          color: var(--zion-bright-gold);
        }

        /* ==========================================
           CONTACT
        ========================================== */

        .zion-footer-contact-section {
          position: relative;
          z-index: 2;
          min-width: 0;
        }

        .zion-footer-contact-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 13px;
          min-width: 0;
        }

        .zion-footer-contact-card {
          display: flex;
          align-items: flex-start;
          gap: 13px;
          min-width: 0;
          padding: 12px;
          border: 1px solid rgba(244, 197, 66, 0.10);
          border-radius: 9px;
          background: rgba(255, 255, 255, 0.025);
          transition: all 0.3s ease;
        }

        .zion-footer-contact-card:hover {
          border-color: rgba(244, 197, 66, 0.30);
          background: rgba(212, 160, 23, 0.045);
          transform: translateX(4px);
        }

        .zion-footer-contact-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          border: 1px solid rgba(244, 197, 66, 0.12);
          border-radius: 8px;
          background: rgba(212, 160, 23, 0.13);
          color: var(--zion-bright-gold);
          font-size: 13px;
        }

        .zion-footer-contact-card > div:last-child {
          min-width: 0;
        }

        .zion-footer-contact-card span {
          display: block;
          margin-bottom: 4px;
          color: #89857c;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .zion-footer-contact-card p {
          margin: 0;
          color: #d1cec6;
          font-size: 12px;
          line-height: 1.6;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        /* ==========================================
           SEND MESSAGE
        ========================================== */

        .zion-footer-connect {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 35px;
          width: 100%;
          max-width: 1340px;
          margin: 0 auto;
          padding: 28px 35px;
          border-top: 1px solid rgba(244, 197, 66, 0.12);
          border-bottom: 1px solid rgba(244, 197, 66, 0.12);
        }

        .zion-footer-connect-content {
          display: flex;
          align-items: center;
          gap: 15px;
          flex: 1 1 340px;
          min-width: 0;
        }

        .zion-footer-connect-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 46px;
          height: 46px;
          flex-shrink: 0;
          border-radius: 11px;
          background: linear-gradient(
            135deg,
            var(--zion-rich-gold),
            var(--zion-bright-gold)
          );
          color: var(--zion-black);
          font-size: 17px;
        }

        .zion-footer-connect-content > div:last-child {
          min-width: 0;
        }

        .zion-footer-connect-content h4 {
          margin: 0 0 4px;
          color: var(--zion-white);
          font-size: 15px;
        }

        .zion-footer-connect-content p {
          margin: 0;
          color: #88847b;
          font-size: 12px;
          line-height: 1.6;
        }

        .zion-footer-newsletter {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: stretch;
          gap: 10px;
          width: min(100%, 650px);
          flex: 1 1 650px;
          min-width: 0;
          border: 0;
          overflow: visible;
          background: transparent;
        }

        .zion-footer-newsletter textarea {
          display: block;
          width: 100%;
          min-width: 0;
          min-height: 88px;
          max-height: 150px;
          padding: 14px 16px;
          border: 1px solid rgba(244, 197, 66, 0.14);
          border-radius: 8px;
          outline: none;
          resize: vertical;
          background: #0e0e0e;
          color: var(--zion-white);
          font-family: inherit;
          font-size: 12px;
          line-height: 1.6;
          transition:
            border-color 0.3s ease,
            box-shadow 0.3s ease;
        }

        .zion-footer-newsletter textarea:focus {
          border-color: rgba(244, 197, 66, 0.55);
          box-shadow: 0 0 0 3px rgba(244, 197, 66, 0.08);
        }

        .zion-footer-newsletter textarea::placeholder {
          color: #66615a;
        }

        .zion-footer-newsletter button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          align-self: stretch;
          min-width: 145px;
          min-height: 88px;
          padding: 14px 20px;
          border: 0;
          border-radius: 8px;
          background: linear-gradient(
            135deg,
            var(--zion-rich-gold),
            var(--zion-bright-gold)
          );
          color: var(--zion-black);
          cursor: pointer;
          font-family: inherit;
          font-size: 12px;
          font-weight: 800;
          transition: all 0.3s ease;
        }

        .zion-footer-newsletter button:hover {
          background: var(--zion-bright-gold);
          color: var(--zion-black);
          transform: translateY(-1px);
          box-shadow: 0 7px 18px rgba(212, 160, 23, 0.18);
        }

        .zion-footer-newsletter button svg {
          font-size: 11px;
          flex-shrink: 0;
        }

        .zion-footer-success {
          display: flex;
          align-items: center;
          justify-content: center;
          width: min(100%, 650px);
          min-height: 88px;
          padding: 16px;
          border: 1px solid rgba(244, 197, 66, 0.22);
          border-radius: 8px;
          background: rgba(212, 160, 23, 0.06);
          color: var(--zion-bright-gold);
          font-size: 12px;
          font-weight: 700;
          text-align: center;
        }

        /* ==========================================
           MOTTO
        ========================================== */

        .zion-footer-motto {
          display: flex;
          align-items: center;
          gap: 20px;
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          padding: 38px 25px;
        }

        .zion-footer-motto span {
          height: 1px;
          flex: 1;
          background: linear-gradient(
            to right,
            transparent,
            rgba(244, 197, 66, 0.45)
          );
        }

        .zion-footer-motto span:last-child {
          background: linear-gradient(
            to left,
            transparent,
            rgba(244, 197, 66, 0.45)
          );
        }

        .zion-footer-motto p {
          margin: 0;
          color: var(--zion-bright-gold);
          font-size: 12px;
          font-weight: 700;
          text-align: center;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          white-space: nowrap;
        }

        /* ==========================================
           BOTTOM
        ========================================== */

        .zion-footer-bottom {
          background: var(--zion-deep-black);
          border-top: 1px solid rgba(244, 197, 66, 0.08);
        }

        .zion-footer-bottom-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 25px;
          width: 100%;
          max-width: 1450px;
          margin: 0 auto;
          padding: 22px 55px;
        }

        .zion-footer-bottom-inner > p {
          margin: 0;
          color: #707070;
          font-size: 11px;
          line-height: 1.6;
        }

        .zion-footer-bottom-links {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .zion-footer-bottom-links a {
          color: #77736c;
          font-size: 11px;
          text-decoration: none;
          transition: 0.3s ease;
        }

        .zion-footer-bottom-links a:hover {
          color: var(--zion-bright-gold);
        }

        .zion-footer-bottom-links span {
          color: #33302c;
          font-size: 11px;
        }

        /* ==========================================
           SCHOOL BADGE
        ========================================== */

        .zion-footer-training-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          max-width: 100%;
          margin-top: 20px;
          padding: 9px 13px;
          border-left: 3px solid var(--zion-bright-gold);
          background: rgba(212, 160, 23, 0.10);
          color: #ddd9cf;
          font-size: 11px;
          font-weight: 600;
          line-height: 1.5;
        }

        .zion-footer-training-badge svg {
          color: var(--zion-bright-gold);
          flex-shrink: 0;
        }

        /* ==========================================
           RESPONSIVE
        ========================================== */

        @media (max-width: 1100px) {
          .zion-footer-main {
            grid-template-columns: 1fr 1fr;
            gap: 45px;
          }

          .zion-footer-brand-section {
            grid-column: span 2;
          }

          .zion-footer-description {
            max-width: 800px;
          }

          .zion-footer-navigation-section {
            grid-template-columns: 1fr 1fr;
          }

          .zion-footer-connect {
            margin-left: 28px;
            margin-right: 28px;
            width: calc(100% - 56px);
          }

          .zion-footer-bottom-inner {
            padding-left: 45px;
            padding-right: 45px;
          }
        }

        @media (max-width: 800px) {
          .zion-footer-main {
            grid-template-columns: 1fr;
            padding: 55px 28px 45px;
            gap: 42px;
          }

          .zion-footer-brand-section {
            grid-column: auto;
          }

          .zion-footer-navigation-section {
            grid-template-columns: 1fr 1fr;
          }

          .zion-footer-connect {
            flex-direction: column;
            align-items: stretch;
            gap: 22px;
            width: calc(100% - 56px);
            margin-left: 28px;
            margin-right: 28px;
            padding: 28px;
          }

          .zion-footer-connect-content {
            width: 100%;
            flex: none;
          }

          .zion-footer-newsletter,
          .zion-footer-success {
            width: 100%;
            flex: none;
          }

          .zion-footer-bottom-inner {
            flex-direction: column;
            align-items: flex-start;
            padding: 22px 28px;
          }
        }

        @media (max-width: 600px) {
          .zion-footer-main {
            padding: 50px 20px 42px;
            gap: 38px;
          }

          .zion-footer-brand {
            align-items: flex-start;
            gap: 13px;
          }

          .zion-footer-brand-icon {
            width: 50px;
            height: 50px;
            font-size: 23px;
          }

          .zion-footer-brand-text h2 {
            font-size: 18px;
          }

          .zion-footer-brand-text span {
            font-size: 9px;
            letter-spacing: 1px;
            line-height: 1.5;
          }

          .zion-footer-description {
            font-size: 13px;
            line-height: 1.8;
          }

          .zion-footer-values {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .zion-footer-value {
            width: 100%;
            min-height: 40px;
            padding: 8px 10px;
            font-size: 11px;
          }

          .zion-footer-navigation-section {
            grid-template-columns: 1fr;
            gap: 35px;
          }

          .zion-footer-contact-grid {
            grid-template-columns: 1fr;
          }

          .zion-footer-social-area {
            align-items: flex-start;
            flex-direction: column;
            gap: 12px;
          }

          .zion-footer-training-badge {
            font-size: 10px;
          }

          .zion-footer-connect {
            width: calc(100% - 40px);
            margin-left: 20px;
            margin-right: 20px;
            padding: 24px 18px;
            gap: 18px;
          }

          .zion-footer-connect-content {
            align-items: flex-start;
          }

          .zion-footer-connect-icon {
            width: 42px;
            height: 42px;
            font-size: 15px;
          }

          .zion-footer-connect-content h4 {
            font-size: 14px;
          }

          .zion-footer-connect-content p {
            font-size: 11px;
            line-height: 1.55;
          }

          .zion-footer-newsletter {
            display: grid;
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .zion-footer-newsletter textarea {
            width: 100%;
            min-height: 110px;
            max-height: 180px;
            padding: 13px 14px;
            border-radius: 8px;
          }

          .zion-footer-newsletter button {
            width: 100%;
            min-width: 0;
            min-height: 46px;
            height: 46px;
            padding: 0 16px;
            border-radius: 8px;
          }

          .zion-footer-success {
            width: 100%;
            min-height: 90px;
            padding: 14px;
            font-size: 11px;
            line-height: 1.6;
          }

          .zion-footer-motto {
            padding: 30px 20px;
          }

          .zion-footer-motto span {
            display: none;
          }

          .zion-footer-motto p {
            width: 100%;
            white-space: normal;
            line-height: 1.6;
            font-size: 11px;
          }

          .zion-footer-bottom-inner {
            padding: 22px 20px;
            align-items: center;
            text-align: center;
          }

          .zion-footer-bottom-inner > p {
            max-width: 100%;
          }

          .zion-footer-bottom-links {
            justify-content: center;
            width: 100%;
            gap: 8px;
          }
        }

        @media (max-width: 480px) {
          .zion-footer-main {
            padding-left: 18px;
            padding-right: 18px;
          }

          .zion-footer-values {
            grid-template-columns: 1fr;
          }

          .zion-footer-brand-text h2 {
            font-size: 17px;
          }

          .zion-footer-description {
            font-size: 12px;
          }

          .zion-footer-contact-card {
            padding: 11px;
          }

          .zion-footer-contact-icon {
            width: 34px;
            height: 34px;
          }

          .zion-footer-connect {
            width: calc(100% - 28px);
            margin-left: 14px;
            margin-right: 14px;
            padding: 22px 15px;
          }

          .zion-footer-newsletter textarea {
            min-height: 105px;
          }

          .zion-footer-motto {
            padding-left: 18px;
            padding-right: 18px;
          }
        }

        @media (max-width: 400px) {
          .zion-footer-main {
            padding-left: 15px;
            padding-right: 15px;
          }

          .zion-footer-brand {
            gap: 11px;
          }

          .zion-footer-brand-icon {
            width: 46px;
            height: 46px;
            font-size: 20px;
          }

          .zion-footer-brand-text h2 {
            font-size: 16px;
          }

          .zion-footer-brand-text span {
            font-size: 8px;
            letter-spacing: 0.8px;
          }

          .zion-footer-title {
            gap: 9px;
          }

          .zion-footer-title-icon {
            width: 31px;
            height: 31px;
          }

          .zion-footer-title h3 {
            font-size: 15px;
          }

          .zion-footer-column li a {
            font-size: 12px;
          }

          .zion-footer-connect {
            width: calc(100% - 20px);
            margin-left: 10px;
            margin-right: 10px;
            padding: 20px 13px;
          }

          .zion-footer-connect-content {
            gap: 11px;
          }

          .zion-footer-connect-icon {
            width: 40px;
            height: 40px;
          }

          .zion-footer-connect-content p {
            font-size: 10px;
          }

          .zion-footer-newsletter textarea {
            min-height: 100px;
            font-size: 11px;
          }

          .zion-footer-newsletter button {
            font-size: 11px;
          }

          .zion-footer-bottom-inner {
            padding: 20px 15px;
          }

          .zion-footer-bottom-links {
            flex-direction: column;
            align-items: center;
            width: 100%;
          }

          .zion-footer-bottom-links span {
            display: none;
          }
        }

        @media (max-width: 340px) {
          .zion-footer-brand {
            align-items: flex-start;
          }

          .zion-footer-brand-icon {
            width: 42px;
            height: 42px;
            font-size: 18px;
            border-radius: 11px;
          }

          .zion-footer-brand-text h2 {
            font-size: 15px;
          }

          .zion-footer-brand-text span {
            font-size: 7px;
          }

          .zion-footer-value {
            font-size: 10px;
          }

          .zion-footer-contact-card p {
            font-size: 11px;
          }

          .zion-footer-motto p {
            font-size: 10px;
          }
        }
      `}</style>

      <div className="zion-footer-accent">
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div className="zion-footer-main">

        {/* BRAND SECTION */}

        <div className="zion-footer-brand-section">

          <div className="zion-footer-brand">

            <div className="zion-footer-brand-icon">
              <FaGraduationCap />
            </div>

            <div className="zion-footer-brand-text">

              <h2>
                Zion Praise
                <br />
                Academy
              </h2>

              <span>
                Excellence • Character • Education
              </span>

            </div>

          </div>

          <p className="zion-footer-description">
            Zion Praise Academy is committed to providing quality
            education in a safe, supportive, and inspiring learning
            environment where students are equipped with knowledge,
            character, confidence, and skills for the future.
          </p>

          <div className="zion-footer-values">

            <div className="zion-footer-value">
              <FaBookOpen />
              <span>Quality Education</span>
            </div>

            <div className="zion-footer-value">
              <FaUsers />
              <span>Safe Learning</span>
            </div>

            <div className="zion-footer-value">
              <FaGraduationCap />
              <span>Future Ready</span>
            </div>

            <div className="zion-footer-value">
              <FaLaptopCode />
              <span>Modern Learning</span>
            </div>

          </div>

          <div className="zion-footer-training-badge">
            <FaCheckCircle />
            Building Knowledge, Character & Excellence
          </div>

          <div className="zion-footer-social-area">

            <span>
              Follow Us
            </span>

            <div className="zion-footer-social-links">

              <a
                href="https://facebook.com/"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>

              <a
                href="https://youtube.com/"
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>

              <a
                href="mailto:info@zionpraiseacademy.edu"
                aria-label="Email"
              >
                <FaEnvelope />
              </a>

            </div>

          </div>

        </div>

        {/* NAVIGATION */}

        <div className="zion-footer-navigation-section">

          <div className="zion-footer-column">

            <div className="zion-footer-title">

              <span className="zion-footer-title-icon">
                <FaArrowRight />
              </span>

              <h3>
                Quick Links
              </h3>

            </div>

            <ul>

              <li>
                <a href="#home">
                  <FaChevronRight />
                  <span>Home</span>
                </a>
              </li>

              <li>
                <a href="#about">
                  <FaChevronRight />
                  <span>About Us</span>
                </a>
              </li>

              <li>
                <a href="#courses">
                  <FaChevronRight />
                  <span>Programs</span>
                </a>
              </li>

              <li>
                <a href="#admissions">
                  <FaChevronRight />
                  <span>Admissions</span>
                </a>
              </li>

              <li>
                <a href="#contact">
                  <FaChevronRight />
                  <span>Contact Us</span>
                </a>
              </li>

            </ul>

          </div>

          <div className="zion-footer-column">

            <div className="zion-footer-title">

              <span className="zion-footer-title-icon">
                <FaBookOpen />
              </span>

              <h3>
                Academics
              </h3>

            </div>

            <ul>

              <li>
                <a href="#academics">
                  <FaChevronRight />
                  <span>Academic Programs</span>
                </a>
              </li>

              <li>
                <a href="#primary">
                  <FaChevronRight />
                  <span>Primary Education</span>
                </a>
              </li>

              <li>
                <a href="#secondary">
                  <FaChevronRight />
                  <span>Secondary Education</span>
                </a>
              </li>

              <li>
                <a href="#activities">
                  <FaChevronRight />
                  <span>Student Activities</span>
                </a>
              </li>

              <li>
                <a href="#admission">
                  <FaChevronRight />
                  <span>Admissions</span>
                </a>
              </li>

              <li>
                <a href="#contact">
                  <FaChevronRight />
                  <span>School Information</span>
                </a>
              </li>

            </ul>

          </div>

        </div>

        {/* CONTACT */}

        <div className="zion-footer-contact-section">

          <div className="zion-footer-title">

            <span className="zion-footer-title-icon">
              <FaMapMarkerAlt />
            </span>

            <h3>
              Contact Us
            </h3>

          </div>

          <div className="zion-footer-contact-grid">

            <div className="zion-footer-contact-card">

              <div className="zion-footer-contact-icon">
                <FaMapMarkerAlt />
              </div>

              <div>

                <span>
                  Visit Us
                </span>

                <p>
                  New Jerusalem Community,
                  Johnsonville, Monrovia, Liberia
                </p>

              </div>

            </div>

            <div className="zion-footer-contact-card">

              <div className="zion-footer-contact-icon">
                <FaPhoneAlt />
              </div>

              <div>

                <span>
                  Call Us
                </span>

                <p>
                  +231-776-4940-10 / +231-880-5858-55
                </p>

              </div>

            </div>

            <div className="zion-footer-contact-card">

              <div className="zion-footer-contact-icon">
                <FaEnvelope />
              </div>

              <div>

                <span>
                  Email Us
                </span>

                <p>
                  info@zionpraiseacademy.edu
                </p>

              </div>

            </div>

            <div className="zion-footer-contact-card">

              <div className="zion-footer-contact-icon">
                <FaClock />
              </div>

              <div>

                <span>
                  Office Hours
                </span>

                <p>
                  Mon - Fri: 8:00 AM - 4:00 PM
                </p>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* SEND MESSAGE */}

      <div className="zion-footer-connect">

        <div className="zion-footer-connect-content">

          <div className="zion-footer-connect-icon">
            <FaEnvelope />
          </div>

          <div>

            <h4>
              Send Us a Message
            </h4>

            <p>
              Have a question? Send us a message and our team will get back to you.
            </p>

          </div>

        </div>

        {messageSent ? (

          <div className="zion-footer-success">
            ✓ Thank you! Your message has been submitted successfully.
          </div>

        ) : (

          <form
            className="zion-footer-newsletter"
            onSubmit={handleSendMessage}
          >

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message here..."
              aria-label="Message"
              required
            />

            <button
              type="submit"
              aria-label="Send message"
            >
              Send Message
              <FaPaperPlane />
            </button>

          </form>

        )}

      </div>

      {/* MOTTO */}

      <div className="zion-footer-motto">

        <span></span>

        <p>
          Learn • Grow • Lead • Succeed
        </p>

        <span></span>

      </div>

      {/* COPYRIGHT */}

      <div className="zion-footer-bottom">

        <div className="zion-footer-bottom-inner">

          <p>
            © {currentYear} Zion Praise Academy.
            All Rights Reserved.
          </p>

          <div className="zion-footer-bottom-links">

            <a href="#privacy">
              Privacy Policy
            </a>

            <span>|</span>

            <a href="#terms">
              Terms & Conditions
            </a>

            <span>|</span>

            <a href="#support">
              System Support
            </a>

          </div>

        </div>

      </div>

    </footer>
  );
}